import { ColumnDef } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { LuBuilding, LuChevronDown, LuChevronRight } from 'react-icons/lu';

import { ImageWithFallbackIcon } from '@/components/ImageWithFallbackIcon';
import { Spinner } from '@/components/Spinner';
import { ClientDataTable } from '@/components/table/DataTable';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { formatCurrencyHuman, formatPercent } from 'utils';

export interface RestrictedStockWithCategories {
    shareClassId: string;
    shareId: string;
    ticker: string;
    name: string;
    marketValue: number;
    percentage: number;
    logoUrl: string;
    restrictedCategories: Array<{
        categoryId: string;
        categoryLabel: string;
    }>;
}

interface AllRestrictedStocksSectionProps {
    stocks: RestrictedStockWithCategories[];
    isLoading: boolean;
}

export const AllRestrictedStocksSection = ({ stocks, isLoading }: AllRestrictedStocksSectionProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const totalRestrictedMarketStats = stocks.reduce(
        (acc, stock) => ({
            marketValue: acc.marketValue + stock.marketValue,
            percentage: acc.percentage + stock.percentage,
        }),
        { marketValue: 0, percentage: 0 },
    );

    const columns: ColumnDef<RestrictedStockWithCategories>[] = useMemo(
        () => [
            {
                accessorKey: 'ticker',
                header: 'Stock',
                enableSorting: true,
                cell: ({ row }) => (
                    <div className="flex items-center gap-3">
                        <ImageWithFallbackIcon
                            src={row.original.logoUrl}
                            alt={`${row.original.name} logo`}
                            fallbackIcon={LuBuilding}
                            className="h-8 w-8 rounded object-cover"
                        />
                        <div className="flex flex-col">
                            <span className="text-cocoa-1000 font-semibold">{row.original.ticker}</span>
                            <span className="text-xs text-gray-600">{row.original.name}</span>
                        </div>
                    </div>
                ),
            },
            {
                id: 'restrictedCategories',
                header: 'Restricted Category',
                enableSorting: false,
                cell: ({ row }) => (
                    <div className="flex flex-wrap gap-1">
                        {row.original.restrictedCategories.map((category) => (
                            <span
                                key={category.categoryId}
                                className="rounded-full px-2 py-1 text-xs font-medium"
                                style={{
                                    backgroundColor: 'rgba(125, 85, 43, 0.15)',
                                    color: 'rgb(125, 85, 43)',
                                }}
                            >
                                {category.categoryLabel}
                            </span>
                        ))}
                    </div>
                ),
            },
            {
                accessorKey: 'marketValue',
                header: 'Market Cap',
                enableSorting: true,
                cell: ({ row }) => (
                    <div className="flex flex-col">
                        <span className="text-cocoa-1000 font-medium">
                            {formatCurrencyHuman(row.original.marketValue, 'USD')}
                        </span>
                        <span className="text-xs text-gray-600">
                            {formatPercent(row.original.percentage)} of universe
                        </span>
                    </div>
                ),
                sortDescFirst: true,
            },
        ],
        [],
    );

    return (
        <div>
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <div className="rounded-lg border bg-white">
                    <div className="flex items-center justify-between bg-white p-4 transition-colors hover:bg-gray-50">
                        <CollapsibleTrigger className="flex flex-1 items-center gap-4 text-left">
                            <div>
                                <h3 className="text-cocoa-1000 font-semibold">All Restricted Securities</h3>
                                {isLoading ? (
                                    <div className="flex items-center gap-2">
                                        <Spinner size="sm" />
                                        <p className="text-sm text-gray-500">Loading restricted stocks...</p>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500">
                                        {stocks.length > 0
                                            ? `${stocks.length} stocks restricted, ${formatCurrencyHuman(totalRestrictedMarketStats.marketValue, 'USD')} market value, ${formatPercent(totalRestrictedMarketStats.percentage)} of universe`
                                            : 'No restricted stocks'}
                                    </p>
                                )}
                            </div>
                        </CollapsibleTrigger>
                        <div className="flex items-center gap-2">
                            {isOpen ? (
                                <LuChevronDown className="h-5 w-5 text-gray-400" />
                            ) : (
                                <LuChevronRight className="h-5 w-5 text-gray-400" />
                            )}
                        </div>
                    </div>

                    <CollapsibleContent>
                        <div className="border-t p-6">
                            <ClientDataTable
                                data={stocks}
                                columns={columns}
                                isLoading={isLoading}
                                loadingRowCount={5}
                                emptyMessage="No restricted stocks found"
                                initialState={{ sorting: [{ id: 'marketValue', desc: true }] }}
                            />
                        </div>
                    </CollapsibleContent>
                </div>
            </Collapsible>
        </div>
    );
};
